import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, Download,
  X, Info, Activity, Sparkles, ArrowRight,
} from "lucide-react";
import { useUserAuth } from "../lib/userAuth";
import useExperiment from "../lib/useExperiment";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const SAMPLE_CSV = `chain,address,label
ethereum,0x742d35Cc6634C0532925a3b844Bc454e4438f44e,Treasury
bitcoin,bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh,Cold storage
solana,9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM,Hot wallet
arbitrum,0x912CE59144191C1204E64559FE8253a0e49E6548,Treasury L2
`;

const TIER_COLOR = {
  low:      "#1FDD83",
  medium:   "#FFB000",
  moderate: "#FFB000",
  high:     "#FF8000",
  critical: "#FF6E6E",
  unknown:  "#5D7394",
};

// Atlas portfolio CSV import — drop a CSV of {chain,address,label} rows and
// the backend batch-scans them sequentially. Polls the job until done.
//
// `usage` (optional): the parent's quota snapshot. When supplied AND the
// account is on the free plan, we render the early upgrade-nudge banner
// the moment they start their first import — surfacing the upsell well
// before they hit the 402 paywall on the second import.
export default function AtlasCsvImport({ onUsageRefresh, usage } = {}) {
  const { account, loading: authLoading } = useUserAuth();
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [paywallInfo, setPaywallInfo] = useState(null);   // 402 detail
  const [uploading, setUploading] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  // Early upgrade nudge — fires once we know the user is on the free plan
  // AND they've kicked off (or finished) their only allowed import. Shows
  // up while the job is processing so the upsell lands during the dwell
  // time when they're already engaged with the product, not after the
  // gate slams shut. Persisted-dismissed state is intentionally per-tab
  // so the nudge re-appears on next visit if they ignore it once.
  const showEarlyNudge =
    !nudgeDismissed &&
    !paywallInfo &&
    usage?.plan === "free" &&
    usage?.kind === "lifetime" &&
    (uploading || !!job) &&
    typeof usage?.remaining === "number" &&
    usage.remaining <= 1;

  // A/B/N — copy on the early-nudge banner is registry-driven so we can
  // measure which framing converts best (urgency vs value vs control).
  // The hook only fires its `view` event when `showEarlyNudge` is true,
  // so impressions correctly attribute to free users actually upload-
  // ing — not every page-load.
  const atlasNudgeExp = useExperiment("atlas-nudge", {
    tier:     "default",
    source:   "atlas-import-early-nudge",
    fallback: {
      label:    "LAST FREE IMPORT — UPGRADE NOW & KEEP GOING",
      headline: "This is your one free Atlas portfolio import.",
      cta:      "Lock in Pro now",
    },
    viewWhen: showEarlyNudge,
  });

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopPoll(), []);

  const pollJob = (jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/atlas/imports/${jobId}`,
                                          { withCredentials: true });
        setJob(data);
        if (data.status === "done" || data.status === "failed") stopPoll();
      } catch (e) {
        stopPoll();
        setError(e?.response?.data?.detail || "Lost track of import job.");
      }
    }, 1500);
  };

  const onFile = async (file) => {
    if (!file) return;
    setError("");
    if (!account) { setError("Sign in to import a portfolio."); return; }
    if (file.size > 200_000) { setError("CSV too large (max 200 KB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/api/atlas/import`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob(data);
      pollJob(data.id);
      // Tell the parent so the usage card / paywall card can refresh.
      if (data?.usage_after && onUsageRefresh) onUsageRefresh(data.usage_after);
    } catch (e) {
      // 402 → paywall: surface the upgrade nudge inline.
      if (e?.response?.status === 402) {
        setPaywallInfo(e.response.data?.detail || { message: "Atlas quota exhausted." });
      } else {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : (detail?.message || "Upload failed."));
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "qortrace-portfolio-sample.csv";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const reset = () => { setJob(null); setError(""); stopPoll(); };

  if (authLoading) return null;

  return (
    <section className="qt-card mt-6" data-testid="atlas-csv-import-section"
             style={{ border: "1px solid var(--qt-border)" }}>
      <div className="flex items-start gap-3 flex-wrap mb-3">
        <FileSpreadsheet size={20} style={{ color: "var(--qt-cyan)" }} className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-[220px]">
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
            BULK IMPORT · CSV
          </div>
          <h3 className="font-bold text-base mt-1" style={{ color: "#fff" }}>
            Batch-scan a portfolio of addresses
          </h3>
          <p className="text-sm mt-1" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
            Upload a CSV of <code className="qt-mono text-xs"
                                  style={{ color: "var(--qt-cyan)" }}>chain,address,label</code> rows
            and we'll scan all of them in the background. Up to 100 addresses per import.
          </p>
        </div>
        <button type="button"
                onClick={downloadSample}
                data-testid="atlas-import-sample"
                className="qt-btn qt-btn-secondary"
                style={{ fontSize: 12, padding: "0.4rem 0.75rem" }}>
          <Download size={12} /> Sample CSV
        </button>
      </div>

      {!account && (
        <div className="qt-card mb-3" style={{ background: "rgba(255,176,0,0.06)", border: "1px solid rgba(255,176,0,0.3)", padding: "0.75rem 1rem" }}>
          <div className="flex items-center gap-2">
            <Info size={13} style={{ color: "#FFB000" }} />
            <span className="text-xs" style={{ color: "#FFB000" }}>
              Sign in to use bulk import.
            </span>
          </div>
        </div>
      )}

      {showEarlyNudge && (
        <div
          data-testid="atlas-import-early-nudge"
          className="qt-card mb-3 atlas-nudge-fadein"
          style={{
            background: "linear-gradient(135deg, rgba(0,229,255,0.08), rgba(0,229,255,0.02))",
            border: "1px solid rgba(0,229,255,0.45)",
            padding: "0.85rem 1rem",
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ background: "rgba(0,229,255,0.15)", border: "1px solid rgba(0,229,255,0.5)" }}
            >
              <Sparkles size={14} style={{ color: "var(--qt-cyan)" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="qt-label"
                style={{ color: "var(--qt-cyan)" }}
                data-variant={atlasNudgeExp.variant}
              >
                {atlasNudgeExp.copy.label}
              </div>
              <div className="text-sm mt-1" style={{ color: "#fff", lineHeight: 1.55 }}>
                {atlasNudgeExp.copy.headline} Pro ($199/mo) gets you{" "}
                <span style={{ color: "var(--qt-cyan)", fontWeight: 600 }}>25 portfolios every month</span>{" "}
                with API + webhooks, and Team ($999/mo) opens up{" "}
                <span style={{ color: "#FFB800", fontWeight: 600 }}>200/month with 6 seats</span>.
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <a
                  href="/pricing"
                  data-testid="atlas-import-early-nudge-cta"
                  data-variant={atlasNudgeExp.variant}
                  onClick={atlasNudgeExp.recordClick}
                  className="qt-btn qt-btn-primary"
                  style={{ fontSize: 12, padding: "0.4rem 0.85rem" }}
                >
                  {atlasNudgeExp.copy.cta} <ArrowRight size={12} />
                </a>
                <a
                  href="/audit/talk-to-sales"
                  data-testid="atlas-import-early-nudge-sales"
                  onClick={atlasNudgeExp.recordClick}
                  className="qt-btn qt-btn-secondary"
                  style={{ fontSize: 12, padding: "0.4rem 0.85rem" }}
                >
                  Talk to sales
                </a>
                <button
                  type="button"
                  onClick={() => setNudgeDismissed(true)}
                  data-testid="atlas-import-early-nudge-dismiss"
                  className="qt-mono text-[11px] opacity-70 hover:opacity-100"
                  style={{ color: "var(--qt-text-2)", letterSpacing: "0.08em", padding: "0.4rem 0.5rem" }}
                >
                  MAYBE LATER
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {paywallInfo && (
        <div
          data-testid="atlas-import-paywall"
          className="qt-card mb-3"
          style={{
            background: "rgba(255,184,0,0.06)",
            border: "1px solid rgba(255,184,0,0.5)",
            padding: "0.85rem 1rem",
          }}
        >
          <div className="qt-label" style={{ color: "#FFB800" }}>ATLAS QUOTA EXHAUSTED</div>
          <div className="text-sm mt-1" style={{ color: "#fff" }}>
            {paywallInfo.message || "Free accounts get 1 Atlas import."} Upgrade to keep batch-scanning portfolios.
          </div>
          <div className="mt-2 flex gap-2">
            <a href={paywallInfo.upgrade_url || "/pricing"}
               className="qt-btn qt-btn-primary"
               style={{ fontSize: 12, padding: "0.4rem 0.85rem" }}
               data-testid="atlas-import-paywall-cta">
              See upgrade plans
            </a>
            <button type="button" onClick={() => setPaywallInfo(null)}
                    className="qt-btn qt-btn-secondary"
                    style={{ fontSize: 12, padding: "0.4rem 0.85rem" }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="qt-card mb-3" data-testid="atlas-import-error"
             style={{ background: "rgba(255,110,110,0.08)", border: "1px solid rgba(255,110,110,0.3)", padding: "0.75rem 1rem" }}>
          <div className="flex items-center gap-2">
            <AlertCircle size={13} style={{ color: "#FF8E8E" }} />
            <span className="text-xs" style={{ color: "#FF8E8E" }}>{error}</span>
          </div>
        </div>
      )}

      {!job && (
        <div
          data-testid="atlas-import-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          style={{
            border: "1.5px dashed var(--qt-border)",
            borderRadius: 8,
            padding: "2rem 1rem",
            textAlign: "center",
            cursor: account ? "pointer" : "not-allowed",
            opacity: account ? 1 : 0.6,
            background: "rgba(255,255,255,0.015)",
            transition: "border-color 200ms",
          }}
          onMouseEnter={(e) => { if (account) e.currentTarget.style.borderColor = "var(--qt-cyan)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--qt-border)"; }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="atlas-import-file-input"
            disabled={!account || uploading}
            onChange={(e) => onFile(e.target.files?.[0])}
            style={{ display: "none" }}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2"
                 style={{ color: "var(--qt-cyan)" }}>
              <Loader2 size={14} className="animate-spin" />
              <span className="qt-mono text-xs" style={{ letterSpacing: "0.10em" }}>UPLOADING…</span>
            </div>
          ) : (
            <>
              <Upload size={18} style={{ color: "var(--qt-cyan)" }} />
              <div className="qt-mono text-xs mt-2"
                   style={{ color: "var(--qt-text-2)", letterSpacing: "0.10em" }}>
                DROP A CSV HERE · OR CLICK TO BROWSE
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--qt-text-3)" }}>
                Max 200 KB · 100 rows · headers: chain, address, label
              </div>
            </>
          )}
        </div>
      )}

      {job && <JobProgress job={job} onReset={reset} />}
    </section>
  );
}

function JobProgress({ job, onReset }) {
  const total = job.total || 0;
  const scanned = job.scanned || 0;
  const pct = total ? Math.round((scanned / total) * 100) : 0;
  const isDone = job.status === "done";
  const isFailed = job.status === "failed";

  return (
    <div data-testid="atlas-import-job">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          {isDone ? (
            <CheckCircle2 size={14} style={{ color: "#1FDD83" }} />
          ) : isFailed ? (
            <AlertCircle size={14} style={{ color: "#FF6E6E" }} />
          ) : (
            <Loader2 size={14} className="animate-spin" style={{ color: "var(--qt-cyan)" }} />
          )}
          <span className="qt-mono text-xs"
                style={{
                  letterSpacing: "0.10em",
                  color: isDone ? "#1FDD83" : isFailed ? "#FF6E6E" : "var(--qt-cyan)",
                  fontWeight: 700,
                }}>
            {isDone ? `IMPORT COMPLETE · ${job.succeeded || 0} / ${total}`
              : isFailed ? "IMPORT FAILED"
              : `SCANNING ${scanned} / ${total}`}
          </span>
        </div>
        <button type="button" onClick={onReset}
                data-testid="atlas-import-reset"
                className="qt-mono text-[11px] inline-flex items-center gap-1"
                style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
          <X size={11} /> NEW IMPORT
        </button>
      </div>

      <div style={{
        height: 6, borderRadius: 3, overflow: "hidden",
        background: "rgba(255,255,255,0.06)",
      }}>
        <div data-testid="atlas-import-progress-bar"
             style={{
               width: `${pct}%`, height: "100%",
               background: isDone ? "#1FDD83" : "var(--qt-cyan)",
               transition: "width 600ms ease",
             }} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        <Stat label="TOTAL"      value={total} />
        <Stat label="SUCCEEDED"  value={job.succeeded || 0} accent="#1FDD83" />
        <Stat label="FAILED"     value={job.failed || 0}    accent="#FF6E6E" />
        <Stat label="DEDUPED"    value={(job.dedupe_count || 0) + (job.invalid_count || 0)}
              accent="#5D7394" />
      </div>

      {/* Per-row results table */}
      {(job.rows || []).length > 0 && (
        <div className="mt-4" style={{
          maxHeight: 360, overflow: "auto",
          border: "1px solid var(--qt-border)", borderRadius: 6,
        }}>
          <table className="w-full text-sm" data-testid="atlas-import-rows">
            <thead style={{ position: "sticky", top: 0, background: "#0A1020", zIndex: 1 }}>
              <tr style={{ color: "var(--qt-text-3)" }}>
                <Th>CHAIN</Th><Th>ADDRESS</Th><Th>LABEL</Th><Th>STATUS</Th><Th>SCORE</Th><Th>BAL USD</Th>
              </tr>
            </thead>
            <tbody>
              {job.rows.map((r, i) => <Row key={i} row={r} />)}
            </tbody>
          </table>
        </div>
      )}

      {(job.invalid || []).length > 0 && (
        <details className="mt-3" data-testid="atlas-import-invalid-details">
          <summary className="qt-mono text-[11px] cursor-pointer"
                   style={{ color: "#FFB000", letterSpacing: "0.10em" }}>
            {job.invalid.length} ROW{job.invalid.length === 1 ? "" : "S"} REJECTED · CLICK TO INSPECT
          </summary>
          <div className="text-xs mt-2 space-y-1" style={{ color: "var(--qt-text-2)" }}>
            {job.invalid.map((r, i) => (
              <div key={i}>
                <span className="qt-mono" style={{ color: "var(--qt-text-3)" }}>
                  [{r.chain || "?"}] {r.address?.slice(0, 24)}{(r.address || "").length > 24 ? "…" : ""}
                </span>
                {" — "}{r.error}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function Row({ row }) {
  const tier = (row.tier || "unknown").toLowerCase();
  const isDone = row.status === "done";
  const isFail = row.status === "failed";
  const isScanning = row.status === "scanning";
  const score = row.score;
  const tierColor = TIER_COLOR[tier] || TIER_COLOR.unknown;

  return (
    <tr style={{ borderTop: "1px solid var(--qt-border)" }}
        data-testid={`atlas-import-row-${row.address?.slice(0, 12)}`}>
      <Td>{row.chain}</Td>
      <Td mono>{row.address?.slice(0, 10)}…{row.address?.slice(-6)}</Td>
      <Td>{row.label || "—"}</Td>
      <Td>
        {isDone && (
          <span className="qt-mono text-[10px] px-2 py-0.5 rounded"
                style={{ background: "rgba(0,210,106,0.12)", color: "#1FDD83" }}>
            DONE
          </span>
        )}
        {isFail && (
          <span title={row.error}
                className="qt-mono text-[10px] px-2 py-0.5 rounded"
                style={{ background: "rgba(255,110,110,0.12)", color: "#FF6E6E" }}>
            FAILED
          </span>
        )}
        {isScanning && (
          <span className="qt-mono text-[10px] inline-flex items-center gap-1"
                style={{ color: "var(--qt-cyan)" }}>
            <Activity size={10} /> SCANNING
          </span>
        )}
        {!isDone && !isFail && !isScanning && (
          <span className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)" }}>
            QUEUED
          </span>
        )}
      </Td>
      <Td mono>
        {score != null ? (
          <span style={{ color: tierColor, fontWeight: 700 }}>{score}</span>
        ) : "—"}
      </Td>
      <Td mono>
        {row.balance_usd != null
          ? `$${Number(row.balance_usd).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
          : "—"}
      </Td>
    </tr>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="qt-card" style={{ padding: "0.6rem 0.8rem", background: "rgba(255,255,255,0.02)" }}>
      <div className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)", letterSpacing: "0.12em" }}>
        {label}
      </div>
      <div className="qt-mono mt-1" style={{ fontSize: 18, fontWeight: 700, color: accent || "#fff" }}>
        {value}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="qt-mono text-left p-2 text-[10px]"
             style={{ color: "inherit", letterSpacing: "0.10em" }}>{children}</th>;
}
function Td({ children, mono }) {
  return <td className={`p-2 ${mono ? "qt-mono" : ""}`}
             style={{ color: mono ? "#fff" : "var(--qt-text-2)" }}>{children}</td>;
}
